<template>
  <div v-if="show" class="modal-overlay" @click="$emit('close')">
    <div class="modal-container modal-extra-large" @click.stop>
      <div class="modal-header">
        <h2>Analysis Results</h2>
        <button @click="$emit('close')" class="modal-close">&times;</button>
      </div>
      
      <div class="modal-body">
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Loading results...</span>
        </div>

        <!-- Full-modal in-progress screen — replaces split pane entirely -->
        <div v-else-if="isRunning" class="analysis-running-screen">
          <div class="running-icon-wrap">
            <i class="fas fa-cog fa-spin running-icon"></i>
            <div class="running-pulse"></div>
          </div>
          <h3 class="running-title">Analysis in Progress</h3>
          <p class="running-subtitle">
            The AI is evaluating your document against all requirements.<br>
            Results will appear automatically when complete.
          </p>
          <div class="running-steps">
            <div class="running-step done">
              <i class="fas fa-check-circle"></i>
              <span>Document uploaded</span>
            </div>
            <div class="running-step done">
              <i class="fas fa-check-circle"></i>
              <span>Analysis queued</span>
            </div>
            <div class="running-step active">
              <i class="fas fa-circle-notch fa-spin"></i>
              <span>AI evaluating requirements…</span>
            </div>
            <div class="running-step pending">
              <i class="far fa-circle"></i>
              <span>Compiling results</span>
            </div>
          </div>
          <div class="running-auto-status">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': checkingStatus }"></i>
            <span v-if="checkingStatus">Checking now…</span>
            <span v-else-if="lastCheckedSecondsAgo !== null">
              Checked {{ lastCheckedSecondsAgo }}s ago · checking every 5s
            </span>
            <span v-else>Checking automatically…</span>
          </div>
          <button
            @click="pollNow"
            :disabled="checkingStatus"
            class="btn btn-outline btn-check-now"
          >
            Check now
          </button>
          <p class="running-hint">You can close this and come back — the analysis runs in the background.</p>
        </div>

        <div v-else class="split-pane-container">
          <div class="split-pane-layout">
            <!-- Left Panel: Document Viewer -->
            <div class="document-panel">
              <div class="panel-header">
                <h4><i class="fas fa-file-alt"></i> Source Document</h4>
                <p class="panel-subtitle">Review the analyzed document to validate findings</p>
              </div>
              <div class="document-viewer">
                <div v-if="document" class="document-content">
                  <div class="document-info">
                    <h5>{{ document.name }}</h5>
                    <span class="document-meta">Analysis Date: {{ formatDate(checkRun?.created_at) }}</span>
                  </div>
                  <div class="document-actions">
                    <a :href="getDocumentUrl(document)" 
                       target="_blank" 
                       class="btn btn-outline btn-sm">
                      <i class="fas fa-external-link-alt"></i> View Full Size
                    </a>
                    <button 
                       @click="handleDownload(document)"
                       :disabled="downloading"
                       class="btn btn-outline btn-sm">
                      <i :class="downloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i> 
                      {{ downloading ? 'Downloading...' : 'Download' }}
                    </button>
                  </div>
                  <!-- Document preview -->
                  <div class="document-preview">
                    <div v-if="isPDF(document)" class="pdf-container">
                      <iframe :src="getDocumentUrl(document)" 
                              type="application/pdf"
                              class="pdf-preview"
                              title="Analysis Document Preview">
                      </iframe>
                    </div>
                    <div v-else class="preview-placeholder">
                      <i class="fas fa-file-alt"></i>
                      <p>Preview not available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Panel: Analysis Results -->
            <div class="results-panel">
              <div class="panel-header">
                <h4><i class="fas fa-clipboard-check"></i> Analysis Results</h4>
                <p class="panel-subtitle">Comprehensive check findings and evidence</p>
              </div>
              
              <div class="results-header">
                <div class="result-info">
                  <div><strong>Analysis:</strong> {{ checkRun?.check_definition_name || 'Comprehensive Check' }}</div>
                  <div><strong>Status:</strong> <span :class="['run-status', checkRun?.status?.toLowerCase()]">{{ checkRun?.status }}</span></div>
                </div>
                <div class="result-summary">
                  <div class="summary-card">
                    <div class="summary-title">Overall Outcome</div>
                    <div :class="['summary-value', checkRun?.outcome ? `outcome-${checkRun.outcome.toLowerCase()}` : '']">
                      {{ checkRun?.outcome || '--' }}
                    </div>
                  </div>
                  <div class="summary-card">
                    <div class="summary-title">Passed</div>
                    <div class="summary-value passed">{{ checkRun?.results_summary?.passed || 0 }}</div>
                  </div>
                    <div class="summary-card">
                      <div class="summary-title">Failed</div>
                      <div class="summary-value failed">{{ checkRun?.results_summary?.failed || 0 }}</div>
                    </div>
                  </div>
                  <button
                    v-if="checkRun?.status === 'COMPLETED' && !comparisonRun"
                    type="button"
                    class="btn btn-primary btn-sm"
                    :disabled="comparisonLoading"
                    @click="startComparison"
                  >
                    <i :class="comparisonLoading ? 'fas fa-spinner fa-spin' : 'fas fa-redo'"></i>
                    Re-run and compare
                  </button>
                </div>

                <section v-if="agentOverview || agentInstructions.length" class="notification-panel notification-panel--info">
                  <div class="notification-header">
                    <div class="notification-title">
                      <i class="fas fa-comment-alt"></i>
                      <span>What the agent was told</span>
                    </div>
                    <span v-if="agentMessageSent" class="notification-status sent">Email sent</span>
                    <span v-else class="notification-status pending">Not emailed</span>
                  </div>
                  <p>{{ agentOverview }}</p>
                  <ul v-if="agentInstructions.length">
                    <li v-for="(item, idx) in agentInstructions" :key="'agent-ins-' + idx">
                      {{ item.action }}
                    </li>
                  </ul>
                </section>

                <section v-if="comparisonRun || comparisonLoading" class="notification-panel">
                  <div class="notification-header">
                    <div class="notification-title">
                      <i class="fas fa-columns"></i>
                      <span>Comparison</span>
                    </div>
                    <span v-if="comparisonLoading" class="notification-status pending">
                      Running the current check. The original result stays on file.
                    </span>
                    <span v-else-if="comparisonRun?.run_metadata?.comparison_of" class="notification-status info">
                      Review only. The agent was not emailed, and the franchisee count did not change.
                    </span>
                    <span v-else class="notification-status sent">
                      This result is now the live one. The earlier run is still on file.
                    </span>
                  </div>
                  <p v-if="comparisonError" class="notification-recipient no-recipient">{{ comparisonError }}</p>
                  <template v-if="comparisonRun && !comparisonLoading">
                    <p><strong>New agent summary</strong></p>
                    <p>{{ comparisonOverview }}</p>
                    <ul v-if="comparisonInstructions.length">
                      <li v-for="(item, idx) in comparisonInstructions" :key="'cmp-' + idx">
                        {{ item.action }}
                      </li>
                    </ul>
                    <ul>
                      <li v-for="row in comparisonRows" :key="row.code">
                        <strong>{{ row.name }}</strong>
                        — was {{ row.before }}, now {{ row.after }}
                        <span v-if="row.evidence"> — {{ row.evidence }}</span>
                      </li>
                    </ul>
                    <div v-if="comparisonRoster" class="roster-review">
                      <p class="roster-readout">{{ comparisonRoster.readout }}</p>
                      <div v-if="comparisonRoster.unmatched?.length" class="roster-group">
                        <strong>FranConnect locations not matched</strong>
                        <div
                          v-for="loc in comparisonRoster.unmatched"
                          :key="`cmp-unmatched-${loc.location_id || loc.label}`"
                          class="roster-row"
                          :class="loc.status"
                        >
                          <span class="roster-label">{{ loc.label }}</span>
                          <span v-if="loc.meta_line" class="roster-meta">{{ loc.meta_line }}</span>
                          <span class="roster-reason">{{ loc.reason }}</span>
                        </div>
                      </div>
                    </div>
                    <p>
                      Sending the request emails the new summary to this one agent. The franchisee count stays on the earlier result until you use this result.
                    </p>
                    <div class="purpose-actions">
                      <button
                        v-if="comparisonHasGap && comparisonRun.run_metadata?.comparison_of"
                        type="button"
                        class="btn btn-primary btn-sm"
                        @click="openComparisonSend"
                      >
                        Send this request
                      </button>
                      <button
                        v-if="comparisonRun.run_metadata?.comparison_of"
                        type="button"
                        class="btn btn-outline btn-sm"
                        :disabled="adoptingComparison"
                        @click="adoptComparison"
                      >
                        Use this result
                      </button>
                    </div>
                  </template>
                </section>

              <!-- Notification transparency + recipient management (PR1 + PR2) -->
              <div v-if="notificationPreview" class="notification-panel" :class="notificationPanelClass">
                <div class="notification-header">
                  <div class="notification-title">
                    <i class="fas fa-envelope"></i>
                    <span>Gap Notification Email</span>
                  </div>
                  <span v-if="notificationPreview.auto_sent" class="notification-status sent">
                    <i class="fas fa-check-circle"></i> Auto-sent on completion
                  </span>
                  <span v-else-if="notificationPreview.failed_count === 0" class="notification-status info">
                    <i class="fas fa-info-circle"></i> No gaps — no notification needed
                  </span>
                  <span v-else class="notification-status pending">
                    <i class="fas fa-pause-circle"></i> Not sent automatically
                  </span>
                </div>

                <div v-if="notificationPreview.recipient" class="notification-recipient">
                  <div class="recipient-row">
                    <strong>Suggested recipient:</strong>
                    <span class="recipient-email">{{ notificationPreview.recipient.email }}</span>
                    <span class="recipient-source" :title="`Resolved via: ${notificationPreview.recipient.source}`">
                      ({{ formatSource(notificationPreview.recipient.source) }})
                    </span>
                  </div>
                  <div v-if="notificationPreview.recipient.contact_name" class="recipient-contact">
                    <i class="fas fa-user"></i>
                    {{ notificationPreview.recipient.contact_name }}
                  </div>
                </div>
                <div v-else class="notification-recipient no-recipient">
                  <i class="fas fa-exclamation-triangle"></i>
                  No contact email could be auto-resolved. You can still pick a contact or
                  type one in below.
                </div>

                <div v-if="notificationBlockingExplanations.length > 0" class="notification-reasons">
                  <strong>Why it wasn't auto-sent:</strong>
                  <ul>
                    <li v-for="(reason, idx) in notificationBlockingExplanations" :key="idx">
                      {{ reason }}
                    </li>
                  </ul>
                </div>

                <div v-if="notificationHistory.length > 0" class="notification-history">
                  <strong>Send history:</strong>
                  <ul>
                    <li v-for="(entry, idx) in notificationHistory" :key="entry.id || idx">
                      <i :class="entry.success ? 'fas fa-check text-success' : 'fas fa-times text-danger'"></i>
                      <span class="history-trigger" :class="`history-trigger--${(entry.trigger || '').toLowerCase()}`">
                        {{ entry.trigger || 'MANUAL' }}
                      </span>
                      <span>{{ formatDate(entry.sent_at) }}</span>
                      <span v-if="entry.sent_by_user_email"> — by {{ entry.sent_by_user_email }}</span>
                      <span v-if="entry.recipient"> → {{ entry.recipient }}</span>
                      <span v-if="entry.cc && entry.cc.length > 0">
                        (cc: {{ entry.cc.join(', ') }})
                      </span>
                      <span v-if="!entry.success && entry.reason" class="text-danger">
                        ({{ entry.reason }})
                      </span>
                    </li>
                  </ul>
                </div>

                <!-- Inline send-drawer (PR2): override To/CC, pick a contact -->
                <div v-if="showSendDrawer" class="send-drawer">
                  <div class="drawer-header">
                    <strong><i class="fas fa-paper-plane"></i> Send gap notification</strong>
                  </div>

                  <div class="drawer-row">
                    <label>To <span class="req">*</span></label>
                    <input
                      type="email"
                      v-model.trim="sendForm.to"
                      placeholder="recipient@example.com"
                      class="form-control"
                    />
                  </div>

                  <div v-if="candidateContacts.length > 0" class="drawer-row">
                    <label>Pick from known contacts</label>
                    <select
                      v-model="sendForm.contact_id"
                      @change="onCandidateChosen"
                      class="form-control"
                    >
                      <option :value="null">— Select a contact —</option>
                      <option
                        v-for="c in candidateContacts"
                        :key="`${c.relation}-${c.contact_id || c.email}`"
                        :value="c.contact_id"
                        :data-email="c.email"
                      >
                        {{ c.name }} &lt;{{ c.email }}&gt;
                        <template v-if="c.is_primary"> · primary</template>
                        <template v-if="c.entity_name"> · {{ c.entity_name }}</template>
                      </option>
                    </select>
                  </div>

                  <div class="drawer-row">
                    <label>CC (optional)</label>
                    <div class="cc-input-row">
                      <input
                        type="email"
                        v-model.trim="ccInput"
                        @keydown="onCcKeydown"
                        placeholder="Type email + Enter"
                        class="form-control"
                      />
                      <button @click="addCc" class="btn btn-outline btn-sm" type="button">
                        <i class="fas fa-plus"></i> Add
                      </button>
                    </div>
                    <div v-if="sendForm.cc.length > 0" class="cc-tags">
                      <span v-for="(addr, idx) in sendForm.cc" :key="addr" class="cc-tag">
                        {{ addr }}
                        <button @click="removeCc(idx)" class="cc-remove" type="button" title="Remove">
                          <i class="fas fa-times"></i>
                        </button>
                      </span>
                    </div>
                    <small class="cc-hint">
                      No one is CC'd by default. Add yourself if you want a copy of the outgoing email.
                    </small>
                  </div>

                  <div class="drawer-actions">
                    <button @click="closeSendDrawer" class="btn btn-outline btn-sm" :disabled="sendingNotification" type="button">
                      Cancel
                    </button>
                    <button
                      @click="confirmSend"
                      :disabled="!sendForm.to || sendingNotification"
                      class="btn btn-primary btn-sm"
                      type="button"
                    >
                      <i :class="sendingNotification ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
                      {{ sendingNotification ? 'Sending...' : `Send to ${sendForm.to || '...'}` }}
                    </button>
                  </div>
                </div>

                <div v-else class="notification-actions">
                  <button
                    @click="loadNotificationPreview"
                    :disabled="notificationLoading"
                    class="btn btn-outline btn-sm"
                    title="Refresh recipient lookup"
                  >
                    <i :class="notificationLoading ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
                    Refresh
                  </button>
                  <button
                    @click="openSendDrawer"
                    :disabled="!notificationPreview.can_send"
                    class="btn btn-primary btn-sm"
                    :title="sendButtonTooltip"
                  >
                    <i class="fas fa-paper-plane"></i>
                    Configure &amp; Send
                  </button>
                </div>
              </div>
              
              <div v-if="requirementResults.length > 0" class="requirements-results">
                <h3>Compliance Analysis</h3>
                
                <!-- Failed Results First -->
                <div v-if="failedResults.length > 0" class="result-section failed-section">
                  <h4 class="section-header failed-header">
                    <i class="fas fa-times-circle"></i> Issues Found ({{ failedResults.length }})
                  </h4>
                  <div 
                    v-for="result in failedResults" 
                    :key="result.id" 
                    class="result-item failed-item"
                  >
                    <div class="result-header-row">
                      <span class="req-code-badge">{{ result.requirement_code }}</span>
                      <span class="severity-badge" :class="`severity-${result.requirement_severity?.toLowerCase()}`">
                        {{ result.requirement_severity }}
                      </span>
                    </div>
                    <div class="result-message">
                      <span class="result-icon">❌</span>
                      <div class="result-details">
                        <div v-if="rosterReview(result)" class="roster-review">
                          <p class="roster-readout">{{ rosterReview(result).readout }}</p>
                          <div v-if="rosterReview(result).matched?.length" class="roster-group">
                            <strong>Matched to FranConnect</strong>
                            <div
                              v-for="loc in rosterReview(result).matched"
                              :key="`matched-${loc.location_id || loc.label}`"
                              class="roster-row listed"
                            >
                              <span class="roster-label">{{ loc.label }}</span>
                              <span v-if="loc.meta_line" class="roster-meta">{{ loc.meta_line }}</span>
                              <span v-if="loc.address" class="roster-address">{{ loc.address }}</span>
                              <span class="roster-reason">{{ loc.reason }}</span>
                            </div>
                          </div>
                          <div v-if="rosterReview(result).unmatched?.length" class="roster-group">
                            <strong>FranConnect locations not matched</strong>
                            <div
                              v-for="loc in rosterReview(result).unmatched"
                              :key="`unmatched-${loc.location_id || loc.label}`"
                              class="roster-row"
                              :class="loc.status"
                            >
                              <span class="roster-label">{{ loc.label }}</span>
                              <span v-if="loc.meta_line" class="roster-meta">{{ loc.meta_line }}</span>
                              <span v-if="loc.address" class="roster-address">{{ loc.address }}</span>
                              <span class="roster-reason">{{ loc.reason }}</span>
                            </div>
                          </div>
                          <p
                            v-if="rosterReview(result).unexpected_store_numbers?.length"
                            class="roster-readout"
                          >
                            On the certificate but not on this franchisee’s eligible FranConnect roster:
                            {{ formatStoreNumbers(rosterReview(result).unexpected_store_numbers) }}.
                          </p>
                          <p
                            v-if="rosterReview(result).stores_to_confirm?.length && rosterReview(result).agent_action"
                            class="roster-readout"
                          >
                            {{ rosterReview(result).agent_action }}
                          </p>
                        </div>
                        <div v-else class="evidence-section">
                          <strong>Evidence:</strong>
                          <p>{{ getEvidenceText(result) }}</p>
                        </div>
                        <div v-if="!rosterReview(result) && getReasoning(result)" class="reasoning-section">
                          <strong>Analysis:</strong>
                          <p>{{ getReasoning(result) }}</p>
                        </div>
                        <div v-if="!rosterReview(result) && getDeficiencies(result).length > 0" class="issues-section">
                          <strong>Issues:</strong>
                          <ul>
                            <li v-for="(deficiency, idx) in getDeficiencies(result)" :key="idx">{{ deficiency }}</li>
                          </ul>
                        </div>
                        <div v-if="!rosterReview(result) && getSuggestions(result).length > 0" class="actions-section">
                          <strong>Recommendations:</strong>
                          <ul>
                            <li v-for="(suggestion, idx) in getSuggestions(result)" :key="idx">{{ suggestion }}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <!-- Feedback Actions -->
                    <div class="result-feedback">
                      <div class="feedback-buttons">
                        <button 
                          @click="markAccurate(result)" 
                          :class="['feedback-btn', { active: result.user_feedback_accuracy === 'ACCURATE' }]"
                          title="Mark as accurate"
                        >
                          <i class="fas fa-thumbs-up"></i>
                        </button>
                        <button 
                          @click="markInaccurate(result)" 
                          :class="['feedback-btn', { active: result.user_feedback_accuracy === 'INACCURATE' }]"
                          title="Mark as inaccurate"
                        >
                          <i class="fas fa-thumbs-down"></i>
                        </button>
                        <button 
                          @click="openAgentForResult(result)" 
                          class="feedback-btn agent-btn"
                          title="Ask agent to help correct this"
                        >
                          <i class="fas fa-robot"></i> Ask Agent
                        </button>
                      </div>
                      <span v-if="result.user_feedback_accuracy && result.user_feedback_accuracy !== 'NONE'" class="feedback-status">
                        <i :class="getFeedbackIcon(result.user_feedback_accuracy)"></i>
                        {{ getFeedbackLabel(result.user_feedback_accuracy) }}
                      </span>
                    </div>
                  </div>
                </div>
                
                <!-- Passed Results -->
                <div v-if="passedResults.length > 0" class="result-section passed-section">
                  <h4 class="section-header passed-header">
                    <i class="fas fa-check-circle"></i> Requirements Met ({{ passedResults.length }})
                  </h4>
                  <div 
                    v-for="result in passedResults" 
                    :key="result.id" 
                    class="result-item passed-item"
                  >
                    <div class="result-header-row">
                      <span class="req-code-badge">{{ result.requirement_code }}</span>
                      <span class="severity-badge" :class="`severity-${result.requirement_severity?.toLowerCase()}`">
                        {{ result.requirement_severity }}
                      </span>
                    </div>
                    <div class="result-message">
                      <span class="result-icon">✅</span>
                      <div class="result-details">
                        <div class="evidence-section">
                          <strong>Evidence:</strong>
                          <p>{{ getEvidenceText(result) }}</p>
                        </div>
                        <p v-if="result.details && result.details.internal_review" class="internal-review-note">
                          {{ result.details.internal_review.message }}
                        </p>
                        <div v-if="getReasoning(result)" class="reasoning-section">
                          <strong>Analysis:</strong>
                          <p>{{ getReasoning(result) }}</p>
                        </div>
                      </div>
                    </div>
                    <!-- Feedback Actions for Passed Results -->
                    <div class="result-feedback">
                      <div class="feedback-buttons">
                        <button 
                          @click="markAccurate(result)" 
                          :class="['feedback-btn', { active: result.user_feedback_accuracy === 'ACCURATE' }]"
                          title="Mark as accurate"
                        >
                          <i class="fas fa-thumbs-up"></i>
                        </button>
                        <button 
                          @click="markInaccurate(result)" 
                          :class="['feedback-btn', { active: result.user_feedback_accuracy === 'INACCURATE' }]"
                          title="Mark as inaccurate"
                        >
                          <i class="fas fa-thumbs-down"></i>
                        </button>
                        <button 
                          @click="openAgentForResult(result)" 
                          class="feedback-btn agent-btn"
                          title="Ask agent to help correct this"
                        >
                          <i class="fas fa-robot"></i> Ask Agent
                        </button>
                      </div>
                      <span v-if="result.user_feedback_accuracy && result.user_feedback_accuracy !== 'NONE'" class="feedback-status">
                        <i :class="getFeedbackIcon(result.user_feedback_accuracy)"></i>
                        {{ getFeedbackLabel(result.user_feedback_accuracy) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else class="no-results">
                <i class="fas fa-inbox"></i>
                <p>No requirement results available</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="$emit('close')" class="btn btn-secondary">Close</button>
        <button @click="exportPDF" class="btn btn-primary">
          <i class="fas fa-file-pdf"></i> Export PDF
        </button>
      </div>
      
      <!-- Analysis Agent Chat -->
      <AnalysisAgentChat
        v-if="showAgentChat"
        :requirement-result="selectedResult"
        :check-run="checkRun"
        :document="document"
        :project-id="projectId"
        @correction-submitted="handleCorrectionSubmitted"
        @close="showAgentChat = false"
      />
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import AnalysisAgentChat from './AnalysisAgentChat.vue';

export default {
  name: 'AnalysisResultsModal',
  components: {
    AnalysisAgentChat
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    checkRunId: {
      type: String,
      required: true
    },
    documentId: {
      type: Number,
      required: true
    },
    projectId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      downloading: false,
      checkRun: null,
      document: null,
      requirementResults: [],
      showAgentChat: false,
      selectedResult: null,
      // Notification panel (PR1: transparency, PR2: recipient management)
      notificationPreview: null,
      notificationLoading: false,
      sendingNotification: false,
      showSendDrawer: false,
      sendForm: {
        to: '',
        cc: [],
        contact_id: null,
      },
      ccInput: '',
      sendTarget: 'selected',
      comparisonPreview: null,
      comparisonRun: null,
      comparisonResults: [],
      comparisonLoading: false,
      comparisonError: '',
      comparisonPoll: null,
      adoptingComparison: false,
      checkingStatus: false,
      pollingTimer: null,
      lastCheckedAt: null,
      lastCheckedSecondsAgo: null,
      clockTimer: null,
    };
  },
  computed: {
    failedResults() {
      return this.requirementResults.filter(r => !this.getResultPassed(r));
    },
    passedResults() {
      return this.requirementResults.filter(r => this.getResultPassed(r));
    },
    notificationPanelClass() {
      if (!this.notificationPreview) return '';
      if (this.notificationPreview.auto_sent) return 'notification-panel--sent';
      if (this.notificationPreview.failed_count === 0) return 'notification-panel--info';
      return 'notification-panel--pending';
    },
    notificationBlockingExplanations() {
      const reasons = this.notificationPreview?.blocking_reasons || [];
      const labels = {
        no_compliance_subject: 'No compliance subject is linked to this analysis.',
        no_contact_email: 'No agent contact email could be resolved.',
        no_failures: 'All requirements passed — there are no gaps to report.',
        document_suppressed:
          'This document was uploaded internally and notifications were suppressed for testing.',
        project_opt_out:
          'The project has not opted in to automatic notifications (Project setting: notify_on_upload_completion).',
      };
      return reasons.map((r) => labels[r] || r);
    },
    notificationHistory() {
      // PR2: server now returns the audit history from NotificationLog. Fall
      // back to the legacy results_summary blob for any in-flight check runs
      // that haven't been re-loaded since the migration.
      if (this.notificationPreview?.history) {
        return this.notificationPreview.history;
      }
      return this.checkRun?.results_summary?.notification_history || [];
    },
    candidateContacts() {
      const preview = this.sendTarget === 'comparison'
        ? this.comparisonPreview
        : this.notificationPreview;
      return preview?.candidate_contacts || [];
    },
    sendButtonTooltip() {
      if (!this.notificationPreview) return '';
      if (this.notificationPreview.can_send) {
        const r = this.notificationPreview.recipient?.email || 'a contact you choose';
        return `Open send dialog (defaults to ${r})`;
      }
      const explanations = this.notificationBlockingExplanations;
      if (explanations.length > 0) {
        return `Cannot send: ${explanations.join(' ')}`;
      }
      return 'Cannot send notification';
    },
    isRunning() {
      return ['IN_PROGRESS', 'PENDING', 'PROCESSING'].includes(this.checkRun?.status?.toUpperCase());
    },
    agentOverview() {
      return this.checkRun?.results_summary?.agent_overview || '';
    },
    agentInstructions() {
      const items = this.checkRun?.results_summary?.agent_instructions;
      return Array.isArray(items) ? items : [];
    },
    agentMessageSent() {
      if (this.notificationPreview?.auto_sent) return true;
      return (this.notificationHistory || []).some((entry) => entry.success);
    },
    comparisonOverview() {
      return this.comparisonRun?.results_summary?.agent_overview || '';
    },
    comparisonInstructions() {
      const items = this.comparisonRun?.results_summary?.agent_instructions;
      return Array.isArray(items) ? items : [];
    },
    comparisonHasGap() {
      return Number(this.comparisonRun?.results_summary?.failed_count || 0) > 0;
    },
    comparisonRows() {
      const before = new Map(
        (this.requirementResults || []).map((row) => [
          row.requirement_code || row.requirement?.code,
          row,
        ]),
      );
      return (this.comparisonResults || []).map((row) => {
        const code = row.requirement_code || row.requirement?.code;
        const prior = before.get(code);
        const label = (passed) => (passed ? 'passing' : 'failing');
        const evidence = String(row.formatted_evidence || row.evidence || '')
          .replace(/\s*🎯\s*\*\*Confidence:\*\*\s*\d+%/gi, '')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 180);
        return {
          code,
          name: row.requirement_name || row.requirement?.name || code,
          before: prior ? label(prior.passed) : 'not on the earlier run',
          after: label(row.passed),
          evidence,
        };
      });
    },
    comparisonRoster() {
      const row = (this.comparisonResults || []).find((result) => this.rosterReview(result));
      return row ? this.rosterReview(row) : null;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.checkRunId) {
          this.resetComparison();
          this.loadData();
        }
      }
    },
    checkRunId() {
      this.resetComparison();
      if (this.show && this.checkRunId) {
        this.loadData();
      }
    },
  },
  mounted() {
    if (this.show && this.checkRunId) {
      this.loadData();
    }
  },
  beforeUnmount() {
    this.stopPolling();
    this.stopComparisonPoll();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [runResponse, docResponse] = await Promise.all([
          api.get(`/comprehensive-check-runs/${this.checkRunId}/`),
          api.get(`/documents/${this.documentId}/`),
        ]);
        this.checkRun = runResponse.data;
        this.document = docResponse.data;

        if (!this.isRunning) {
          await this._loadResults();
        } else {
          this.startPolling();
        }
      } catch (error) {
        console.error('Error loading analysis results:', error);
      } finally {
        this.loading = false;
      }
    },

    startPolling() {
      this.stopPolling();
      this.lastCheckedAt = Date.now();
      this.lastCheckedSecondsAgo = 0;

      // Tick the "X seconds ago" display every second
      this.clockTimer = setInterval(() => {
        if (this.lastCheckedAt) {
          this.lastCheckedSecondsAgo = Math.floor((Date.now() - this.lastCheckedAt) / 1000);
        }
      }, 1000);

      this.pollingTimer = setInterval(() => this.pollNow(), 5000);
    },

    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
      if (this.clockTimer) {
        clearInterval(this.clockTimer);
        this.clockTimer = null;
      }
    },

    async pollNow() {
      if (this.checkingStatus) return;
      this.checkingStatus = true;
      try {
        const runResponse = await api.get(`/comprehensive-check-runs/${this.checkRunId}/`);
        this.checkRun = runResponse.data;
        this.lastCheckedAt = Date.now();
        this.lastCheckedSecondsAgo = 0;
        if (!this.isRunning) {
          this.stopPolling();
          await this._loadResults();
        }
      } catch (error) {
        console.error('Polling error:', error);
      } finally {
        this.checkingStatus = false;
      }
    },

    async _loadResults() {
      const resultsResponse = await api.get(`/requirement-results/?check_run_id=${this.checkRunId}`);
      this.requirementResults = resultsResponse.data.results || resultsResponse.data || [];
      this.loadNotificationPreview();
    },

    async loadNotificationPreview() {
      if (!this.checkRunId) return;
      this.notificationLoading = true;
      try {
        const response = await api.get(
          `/comprehensive-check-runs/${this.checkRunId}/notification-preview/`
        );
        this.notificationPreview = response.data;
      } catch (error) {
        console.error('Error loading notification preview:', error);
        this.notificationPreview = null;
      } finally {
        this.notificationLoading = false;
      }
    },

    openSendDrawer() {
      const preview = this.sendTarget === 'comparison'
        ? this.comparisonPreview
        : this.notificationPreview;
      if (!preview?.can_send) return;
      // Prefill from the resolved recipient. Operator can override before sending.
      const recipient = preview.recipient || {};
      this.sendForm = {
        to: recipient.email || '',
        cc: [],
        contact_id: recipient.contact_id || null,
      };
      this.ccInput = '';
      this.showSendDrawer = true;
    },

    closeSendDrawer() {
      if (this.sendingNotification) return;
      this.showSendDrawer = false;
      this.ccInput = '';
      this.sendTarget = 'selected';
    },

    onCandidateChosen() {
      // Sync the To field to the chosen contact's email
      const chosen = this.candidateContacts.find(
        (c) => c.contact_id === this.sendForm.contact_id
      );
      if (chosen) {
        this.sendForm.to = chosen.email;
      }
    },

    onCcKeydown(event) {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        this.addCc();
      }
    },

    addCc() {
      const value = (this.ccInput || '').trim().replace(/,$/, '');
      if (!value) return;
      // Naive email check; the backend validates anyway.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        alert('That doesn\'t look like a valid email address.');
        return;
      }
      const lower = value.toLowerCase();
      if (this.sendForm.cc.some((c) => c.toLowerCase() === lower)) {
        this.ccInput = '';
        return;
      }
      if (this.sendForm.to && this.sendForm.to.toLowerCase() === lower) {
        // Don't CC the To address
        this.ccInput = '';
        return;
      }
      this.sendForm.cc.push(value);
      this.ccInput = '';
    },

    removeCc(idx) {
      this.sendForm.cc.splice(idx, 1);
    },

    async confirmSend() {
      if (!this.sendForm.to) return;
      this.sendingNotification = true;
      try {
        const payload = {
          to: this.sendForm.to,
          cc: this.sendForm.cc,
        };
        if (this.sendForm.contact_id) {
          payload.contact_id = this.sendForm.contact_id;
        }
        const runId = this.sendTarget === 'comparison'
          ? this.comparisonRun.id
          : this.checkRunId;
        const response = await api.post(
          `/comprehensive-check-runs/${runId}/send-notification/`,
          payload,
        );
        if (response.data.sent) {
          alert(`Notification sent to ${response.data.email}.`);
          this.showSendDrawer = false;
        } else {
          alert(`Notification not sent: ${response.data.reason || 'unknown reason'}`);
        }
      } catch (error) {
        console.error('Error sending notification:', error);
        const detail =
          error?.response?.data?.reason ||
          error?.response?.data?.message ||
          error?.message ||
          'Unknown error';
        alert(`Failed to send notification: ${detail}`);
      } finally {
        this.sendingNotification = false;
        try {
          const runId = this.sendTarget === 'comparison'
            ? this.comparisonRun.id
            : this.checkRunId;
          const runResponse = await api.get(`/comprehensive-check-runs/${runId}/`);
          if (this.sendTarget === 'comparison') {
            this.comparisonRun = runResponse.data;
          } else {
            this.checkRun = runResponse.data;
          }
        } catch (_) { /* non-fatal */ }
        this.sendTarget = 'selected';
        await this.loadNotificationPreview();
      }
    },

    resetComparison() {
      this.stopComparisonPoll();
      this.comparisonRun = null;
      this.comparisonResults = [];
      this.comparisonLoading = false;
      this.comparisonError = '';
      this.comparisonPreview = null;
      this.adoptingComparison = false;
      this.sendTarget = 'selected';
    },

    stopComparisonPoll() {
      if (this.comparisonPoll) {
        clearInterval(this.comparisonPoll);
        this.comparisonPoll = null;
      }
    },

    async startComparison() {
      if (!this.checkRunId || this.comparisonLoading) return;
      this.comparisonLoading = true;
      this.comparisonError = '';
      this.comparisonRun = null;
      this.comparisonResults = [];
      try {
        const response = await api.post(
          `/comprehensive-check-runs/${this.checkRunId}/rerun-comparison/`,
        );
        this.comparisonRun = response.data.check_run;
        this.pollComparison();
      } catch (err) {
        this.comparisonError = err?.response?.data?.error || 'Could not start the comparison.';
        this.comparisonLoading = false;
      }
    },

    pollComparison() {
      this.stopComparisonPoll();
      this.comparisonPoll = setInterval(() => {
        this.refreshComparison();
      }, 5000);
      this.refreshComparison();
    },

    async refreshComparison() {
      if (!this.comparisonRun?.id) return;
      try {
        const response = await api.get(`/comprehensive-check-runs/${this.comparisonRun.id}/`);
        this.comparisonRun = response.data;
        if (this.comparisonRun.status === 'FAILED') {
          this.comparisonError = 'The comparison check failed. The original result is unchanged.';
          this.comparisonLoading = false;
          this.stopComparisonPoll();
          return;
        }
        const summaryReady = Boolean(this.comparisonRun.results_summary?.agent_overview)
          || Boolean(this.comparisonRun.results_summary?.generated_at);
        if (this.comparisonRun.status === 'COMPLETED' && summaryReady) {
          await this.loadComparisonResults();
          this.comparisonLoading = false;
          this.stopComparisonPoll();
        }
      } catch (err) {
        this.comparisonError = 'Could not refresh the comparison.';
        this.comparisonLoading = false;
        this.stopComparisonPoll();
      }
    },

    async loadComparisonResults() {
      const response = await api.get(
        `/requirement-results/?check_run_id=${this.comparisonRun.id}`,
      );
      const data = response.data;
      this.comparisonResults = Array.isArray(data) ? data : (data?.results || []);
    },

    async openComparisonSend() {
      if (!this.comparisonRun?.id) return;
      this.sendTarget = 'comparison';
      try {
        const response = await api.get(
          `/comprehensive-check-runs/${this.comparisonRun.id}/notification-preview/`,
        );
        this.comparisonPreview = response.data;
        if (!response.data?.can_send) {
          this.comparisonError = 'No recipient is available for this request yet.';
          this.sendTarget = 'selected';
          return;
        }
        this.openSendDrawer();
      } catch (err) {
        this.comparisonError = 'Could not load the recipient for this request.';
        this.sendTarget = 'selected';
      }
    },

    async adoptComparison() {
      if (!this.comparisonRun?.id || this.adoptingComparison) return;
      this.adoptingComparison = true;
      this.comparisonError = '';
      try {
        const response = await api.post(
          `/comprehensive-check-runs/${this.comparisonRun.id}/adopt-comparison/`,
        );
        this.comparisonRun = response.data;
      } catch (err) {
        this.comparisonError = err?.response?.data?.error || 'Could not use this result.';
      } finally {
        this.adoptingComparison = false;
      }
    },

    formatSource(source) {
      const labels = {
        agent_primary_contact: 'agent primary contact',
        agent_contact: 'agent contact',
        agent_entity_email: 'agent entity email',
        agent_contact_on_insured: 'insurance agent on file for the insured',
        subject_entity_email: 'subject entity email',
      };
      return labels[source] || source;
    },
    
    getResultPassed(result) {
      if (result.passed !== undefined) return result.passed;
      if (result.result_data?.passed !== undefined) return result.result_data.passed;
      if (result.result === 'PASS') return true;
      if (result.result === 'FAIL') return false;
      return null;
    },
    
    getDocumentUrl(doc) {
      // Use unified document URL helper
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      return getDocumentUrl(doc);
    },
    
    async handleDownload(doc) {
      // Use unified download helper with authentication
      const { downloadDocument } = require('@/utils/documentUrlHelper');
      
      this.downloading = true;
      try {
        await downloadDocument(doc, api);
      } catch (error) {
        console.error('Download failed:', error);
        alert('Failed to download document. Please try again.');
      } finally {
        this.downloading = false;
      }
    },
    
    isPDF(doc) {
      // Use unified PDF detection
      const { isPDF } = require('@/utils/documentUrlHelper');
      return isPDF(doc);
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString();
    },
    
    exportPDF() {
      try {
        // Generate HTML report
        const reportHTML = this.generatePDFReportHTML();
        
        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        
        // Check if popup was blocked
        if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
          alert('Popup blocked! Please allow popups for this site and try again.');
          return;
        }
        
        printWindow.document.write(reportHTML);
        printWindow.document.close();
        
        // Wait for content to load, then trigger print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
        
      } catch (err) {
        console.error('Error exporting results:', err);
        alert('Failed to export results: ' + (err.message || 'Unknown error'));
      }
    },
    
    generatePDFReportHTML() {
      // Helper function to safely escape HTML
      const escapeHtml = (text) => {
        if (!text) return '';
        return String(text)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      };

      const documentName = this.document?.name || 'Document';
      const runDate = this.formatDate(this.checkRun?.created_at);
      const checkDefinitionName = this.checkRun?.check_definition_name || 'Comprehensive Check';
      
      // Count results by status
      const passed = this.passedResults.length;
      const failed = this.failedResults.length;
      const totalResults = this.requirementResults.length;
      const passRate = totalResults > 0 ? Math.round((passed / totalResults) * 100) : 0;

      let requirementDetailsHTML = '';
      
      // Failed results first
      this.failedResults.forEach((result) => {
        const evidence = this.getEvidenceText(result) || '';
        const reasoning = this.getReasoning(result) || '';
        const deficiencies = this.getDeficiencies(result) || [];
        const suggestions = this.getSuggestions(result) || [];
        
        requirementDetailsHTML += `
          <div class="requirement-result" style="page-break-inside: avoid; margin-bottom: 20px; border: 1px solid #f8d7da; padding: 15px; border-radius: 5px; border-left: 4px solid #dc3545;">
            <h4 style="margin: 0 0 10px 0; color: #333;">
              <span style="padding: 3px 8px; border-radius: 3px; font-size: 12px; background-color: #e0e7ff; color: #3730a3; margin-right: 10px;">${escapeHtml(result.requirement_code || '')}</span>
              ${escapeHtml(result.requirement_name || 'Requirement')}
            </h4>
            <div style="margin-bottom: 10px;">
              <span style="padding: 3px 8px; border-radius: 3px; font-weight: bold; background-color: #f8d7da; color: #721c24;">❌ FAILED</span>
              <span style="padding: 3px 8px; border-radius: 3px; font-size: 12px; background-color: #fee2e2; color: #991b1b; margin-left: 10px;">${escapeHtml(result.requirement_severity || 'HIGH')}</span>
            </div>
            
            ${evidence ? `
              <div style="margin: 10px 0;">
                <strong>Evidence:</strong>
                <div style="background-color: #f8f9fa; padding: 10px; border-left: 3px solid #dc3545; margin-top: 5px;">${escapeHtml(evidence)}</div>
              </div>
            ` : ''}
            
            ${reasoning ? `
              <div style="margin: 10px 0;">
                <strong>Analysis:</strong>
                <div style="background-color: #f8f9fa; padding: 10px; border-left: 3px solid #6c757d; margin-top: 5px;">${escapeHtml(reasoning)}</div>
              </div>
            ` : ''}
            
            ${Array.isArray(deficiencies) && deficiencies.length > 0 ? `
              <div style="margin: 10px 0;">
                <strong>Issues Found:</strong>
                <ul style="margin: 5px 0 0 20px;">
                  ${deficiencies.map(def => `<li style="margin: 3px 0; color: #721c24;">${escapeHtml(String(def))}</li>`).join('')}
                </ul>
              </div>
            ` : ''}
            
            ${Array.isArray(suggestions) && suggestions.length > 0 ? `
              <div style="margin: 10px 0;">
                <strong>Recommendations:</strong>
                <ul style="margin: 5px 0 0 20px;">
                  ${suggestions.map(sug => `<li style="margin: 3px 0; color: #155724;">${escapeHtml(String(sug))}</li>`).join('')}
                </ul>
              </div>
            ` : ''}
          </div>
        `;
      });
      
      // Passed results
      this.passedResults.forEach((result) => {
        const evidence = this.getEvidenceText(result) || '';
        const reasoning = this.getReasoning(result) || '';
        
        requirementDetailsHTML += `
          <div class="requirement-result" style="page-break-inside: avoid; margin-bottom: 20px; border: 1px solid #d4edda; padding: 15px; border-radius: 5px; border-left: 4px solid #28a745;">
            <h4 style="margin: 0 0 10px 0; color: #333;">
              <span style="padding: 3px 8px; border-radius: 3px; font-size: 12px; background-color: #e0e7ff; color: #3730a3; margin-right: 10px;">${escapeHtml(result.requirement_code || '')}</span>
              ${escapeHtml(result.requirement_name || 'Requirement')}
            </h4>
            <div style="margin-bottom: 10px;">
              <span style="padding: 3px 8px; border-radius: 3px; font-weight: bold; background-color: #d4edda; color: #155724;">✅ PASSED</span>
              <span style="padding: 3px 8px; border-radius: 3px; font-size: 12px; background-color: #d1fae5; color: #065f46; margin-left: 10px;">${escapeHtml(result.requirement_severity || 'MED')}</span>
            </div>
            
            ${evidence ? `
              <div style="margin: 10px 0;">
                <strong>Evidence:</strong>
                <div style="background-color: #f8f9fa; padding: 10px; border-left: 3px solid #28a745; margin-top: 5px;">${escapeHtml(evidence)}</div>
              </div>
            ` : ''}
            
            ${reasoning ? `
              <div style="margin: 10px 0;">
                <strong>Analysis:</strong>
                <div style="background-color: #f8f9fa; padding: 10px; border-left: 3px solid #6c757d; margin-top: 5px;">${escapeHtml(reasoning)}</div>
              </div>
            ` : ''}
          </div>
        `;
      });

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Comprehensive Analysis Report - ${escapeHtml(documentName)}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              line-height: 1.5; 
              margin: 20px; 
              color: #333; 
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              padding-bottom: 20px; 
              border-bottom: 3px solid #667eea; 
            }
            .header h1 {
              color: #667eea;
              margin-bottom: 10px;
            }
            .summary { 
              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
              padding: 25px; 
              border-radius: 8px; 
              margin-bottom: 30px; 
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .summary-grid { 
              display: grid; 
              grid-template-columns: repeat(4, 1fr); 
              gap: 15px; 
              margin-top: 15px; 
            }
            .summary-item { 
              text-align: center; 
              padding: 15px; 
              background: white; 
              border-radius: 8px; 
              border: 1px solid #e2e8f0;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            .summary-value { 
              font-size: 28px; 
              font-weight: bold; 
            }
            .summary-label { 
              font-size: 12px; 
              color: #64748b; 
              margin-top: 5px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .section-title {
              margin: 30px 0 20px 0;
              padding-bottom: 10px;
              border-bottom: 2px solid #e2e8f0;
              color: #1e293b;
            }
            @media print {
              body { margin: 10px; }
              .header { page-break-after: avoid; }
              .summary { page-break-after: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📋 Comprehensive Analysis Report</h1>
            <h2>${escapeHtml(documentName)}</h2>
            <p><strong>Check Definition:</strong> ${escapeHtml(checkDefinitionName)}</p>
            <p><strong>Analysis Date:</strong> ${runDate}</p>
            <p><strong>Status:</strong> ${escapeHtml(this.checkRun?.status || 'N/A')}</p>
          </div>
          
          <div class="summary">
            <h3 style="margin: 0 0 15px 0; color: #1e293b;">Executive Summary</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-value" style="color: #1e293b;">${totalResults}</div>
                <div class="summary-label">Total Checks</div>
              </div>
              <div class="summary-item">
                <div class="summary-value" style="color: #10b981;">${passed}</div>
                <div class="summary-label">Passed</div>
              </div>
              <div class="summary-item">
                <div class="summary-value" style="color: #ef4444;">${failed}</div>
                <div class="summary-label">Failed</div>
              </div>
              <div class="summary-item">
                <div class="summary-value" style="color: ${passRate >= 80 ? '#10b981' : passRate >= 60 ? '#f59e0b' : '#ef4444'};">${passRate}%</div>
                <div class="summary-label">Pass Rate</div>
              </div>
            </div>
          </div>
          
          ${failed > 0 ? `<h3 class="section-title" style="color: #dc3545;">⚠️ Issues Found (${failed})</h3>` : ''}
          ${failed > 0 ? requirementDetailsHTML.split('</div>').slice(0, failed).join('</div>') + '</div>' : ''}
          
          ${passed > 0 ? `<h3 class="section-title" style="color: #28a745;">✅ Requirements Met (${passed})</h3>` : ''}
          ${passed > 0 ? requirementDetailsHTML.split('</div>').slice(failed).join('</div>') : ''}
          
          <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #e2e8f0; text-align: center; color: #64748b; font-size: 12px;">
            <p><strong>Praetorian AI Compliance System</strong></p>
            <p>Report generated on: ${new Date().toLocaleString()}</p>
          </div>
        </body>
        </html>
      `;
    },
    
    // JSON parsing methods for enhanced evidence format
    rosterReview(result) {
      const review = result && result.roster_review;
      if (!review || typeof review !== 'object') return null;
      if (!review.readout && !(review.unmatched || []).length && !(review.matched || []).length) {
        return null;
      }
      return review;
    },

    formatStoreNumbers(stores) {
      return (stores || []).map((store) => `#${store}`).join(', ');
    },

    getEvidenceText(result) {
      if (!result) return 'No evidence provided';
      if (result.roster_review && result.roster_review.evidence_text) {
        return result.roster_review.evidence_text;
      }
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          return enhancedData.evidence || 'No evidence provided';
        } catch (e) {
          // Not JSON, treat as simple text evidence
          return result.evidence;
        }
      }
      
      // Legacy format or simple evidence
      return result.evidence || result.result_data?.evidence || 'No evidence provided';
    },
    
    getReasoning(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return '';
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.reasoning || '';
      } catch (e) {
        return '';
      }
    },
    
    getDeficiencies(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return [];
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.deficiencies || [];
      } catch (e) {
        return [];
      }
    },
    
    getSuggestions(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return [];
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.suggestions || [];
      } catch (e) {
        return [];
      }
    },
    
    // === Feedback Methods ===
    
    async markAccurate(result) {
      try {
        await api.patch(`/requirement-results/${result.id}/feedback/`, {
          accuracy: 'ACCURATE'
        });
        result.user_feedback_accuracy = 'ACCURATE';
        this.$forceUpdate();
      } catch (error) {
        console.error('Error submitting feedback:', error);
        alert('Failed to submit feedback. Please try again.');
      }
    },
    
    async markInaccurate(result) {
      // For quick inaccurate marking, open the agent chat instead
      this.openAgentForResult(result);
    },
    
    openAgentForResult(result) {
      this.selectedResult = result;
      this.showAgentChat = true;
    },
    
    handleCorrectionSubmitted(updatedResult) {
      // Update the result in our local array
      const index = this.requirementResults.findIndex(r => r.id === updatedResult.id);
      if (index !== -1) {
        this.requirementResults[index] = {
          ...this.requirementResults[index],
          ...updatedResult
        };
        this.$forceUpdate();
      }
    },
    
    getFeedbackIcon(feedbackAccuracy) {
      const icons = {
        'ACCURATE': 'fas fa-check-circle text-success',
        'INACCURATE': 'fas fa-times-circle text-danger',
        'PARTIALLY_CORRECT': 'fas fa-exclamation-circle text-warning'
      };
      return icons[feedbackAccuracy] || 'fas fa-question-circle';
    },
    
    getFeedbackLabel(feedbackAccuracy) {
      const labels = {
        'ACCURATE': 'Marked Accurate',
        'INACCURATE': 'Marked Inaccurate',
        'PARTIALLY_CORRECT': 'Partially Correct'
      };
      return labels[feedbackAccuracy] || 'Feedback Provided';
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  padding: 2rem;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 1600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-extra-large {
  max-width: 1600px;
}

.modal-header {
  padding: 1.5rem 2rem;
  border-bottom: 2px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 2rem;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.modal-close:hover {
  opacity: 1;
}

.modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  gap: 1rem;
  color: #64748b;
}

.analysis-running-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  min-height: 420px;
}

.running-icon-wrap {
  position: relative;
  margin-bottom: 1.5rem;
}

.running-icon {
  font-size: 3.5rem;
  color: #3b82f6;
}

.running-pulse {
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  border: 3px solid #bfdbfe;
  animation: pulse-ring 2s ease-out infinite;
}

@keyframes pulse-ring {
  0%   { transform: scale(0.85); opacity: 0.8; }
  70%  { transform: scale(1.3);  opacity: 0; }
  100% { transform: scale(1.3);  opacity: 0; }
}

.running-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem;
}

.running-subtitle {
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 2rem;
}

.running-steps {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 2rem;
  text-align: left;
  min-width: 260px;
}

.running-step {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.9rem;
}

.running-step.done   { color: #16a34a; }
.running-step.active { color: #2563eb; font-weight: 600; }
.running-step.pending { color: #94a3b8; }

.running-auto-status {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 0.75rem;
}

.running-auto-status i {
  font-size: 0.75rem;
  color: #2563eb;
}

.btn-check-now {
  padding: 0.35rem 1rem;
  font-size: 0.8rem;
  color: #64748b;
  border-color: #cbd5e1;
  margin-bottom: 1rem;
}

.btn-check-now:hover {
  background: #f1f5f9;
  color: #334155;
}

.running-hint {
  color: #94a3b8;
  font-size: 0.8rem;
  margin: 0;
}

.split-pane-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.split-pane-layout {
  display: grid;
  grid-template-columns: 50% 50%;
  flex: 1;
  gap: 0;
  min-height: 0;
}

.document-panel,
.results-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.document-panel {
  border-right: 1px solid #e2e8f0;
  background: #f8f9fa;
}

.results-panel {
  background: white;
  overflow-y: auto;
}

.panel-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: white;
  flex-shrink: 0;
}

.panel-header h4 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
}

.panel-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 0.875rem;
}

.document-viewer {
  flex: 1;
  padding: 1.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.document-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.document-info h5 {
  margin: 0 0 0.25rem 0;
  color: #1e293b;
}

.document-meta {
  font-size: 0.875rem;
  color: #64748b;
}

.document-actions {
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0;
}

.document-preview {
  flex: 1;
  min-height: 0;
  border-radius: 8px;
  overflow: hidden;
}

.pdf-container {
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
}

.pdf-preview {
  width: 100%;
  height: 100%;
  border: none;
}

.preview-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.preview-placeholder i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.results-header {
  padding: 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.result-info {
  margin-bottom: 1rem;
}

.result-info div {
  margin-bottom: 0.5rem;
  font-size: 0.9375rem;
}

.run-status {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 500;
}

.run-status.completed {
  background: #d1fae5;
  color: #065f46;
}

.result-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.summary-card {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e2e8f0;
}

.summary-title {
  font-size: 0.8125rem;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
}

.summary-value.passed {
  color: #10b981;
}

.summary-value.failed {
  color: #ef4444;
}

.requirements-results {
  padding: 1.5rem;
}

.requirements-results h3 {
  margin: 0 0 1.5rem 0;
  color: #1e293b;
  font-size: 1.125rem;
}

.result-section {
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px 8px 0 0;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0;
}

.failed-header {
  background: #fee2e2;
  color: #991b1b;
}

.passed-header {
  background: #d1fae5;
  color: #065f46;
}

.result-item {
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-top: none;
  background: white;
}

.result-item:last-child {
  border-radius: 0 0 8px 8px;
}

.failed-item {
  border-left: 4px solid #ef4444;
}

.passed-item {
  border-left: 4px solid #10b981;
}

.result-header-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.req-code-badge {
  padding: 0.25rem 0.75rem;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
}

.severity-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
}

.severity-badge.severity-critical {
  background: #fee2e2;
  color: #991b1b;
}

.severity-badge.severity-high {
  background: #fed7aa;
  color: #9a3412;
}

.severity-badge.severity-medium {
  background: #fef3c7;
  color: #92400e;
}

.result-message {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.result-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.result-details {
  flex: 1;
}

.evidence-section,
.issues-section,
.actions-section {
  margin-bottom: 1rem;
}

.evidence-section strong,
.issues-section strong,
.actions-section strong {
  display: block;
  margin-bottom: 0.5rem;
  color: #475569;
  font-size: 0.875rem;
}

.evidence-section p,
.issues-section p,
.actions-section p {
  margin: 0;
  color: #1e293b;
  line-height: 1.6;
}

.internal-review-note {
  margin: 0 0 1rem;
  color: #92400e;
  background: #fffbeb;
  border-left: 3px solid #d97706;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.roster-review {
  margin-bottom: 1rem;
}

.roster-readout {
  margin: 0 0 0.75rem;
  color: #1e293b;
  line-height: 1.5;
}

.roster-group {
  margin-bottom: 0.75rem;
}

.roster-group strong {
  display: block;
  margin-bottom: 0.35rem;
  color: #475569;
  font-size: 0.8rem;
}

.roster-row {
  padding: 0.55rem 0;
  border-top: 1px solid #e2e8f0;
}

.roster-label {
  display: block;
  font-weight: 600;
  color: #0f172a;
}

.roster-meta,
.roster-address,
.roster-reason {
  display: block;
  font-size: 0.85rem;
  line-height: 1.45;
}

.roster-meta,
.roster-address {
  color: #475569;
}

.roster-reason {
  margin-top: 0.15rem;
  color: #1e293b;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #94a3b8;
}

.no-results i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.modal-footer {
  padding: 1.5rem 2rem;
  border-top: 2px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
}

.btn-secondary {
  background: #e2e8f0;
  color: #475569;
}

.btn-secondary:hover {
  background: #cbd5e1;
}

.btn-outline {
  background: white;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.btn-outline:hover {
  border-color: #667eea;
  color: #667eea;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

/* Feedback Actions */
.result-feedback {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
}

.feedback-buttons {
  display: flex;
  gap: 0.5rem;
}

.feedback-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #64748b;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.feedback-btn:hover {
  background: #f1f5f9;
  color: #334155;
}

.feedback-btn.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.feedback-btn.active:hover {
  background: #5a67d8;
}

.feedback-btn.agent-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-color: #10b981;
}

.feedback-btn.agent-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.feedback-status {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #64748b;
}

.feedback-status .text-success {
  color: #10b981;
}

.feedback-status .text-danger {
  color: #ef4444;
}

.feedback-status .text-warning {
  color: #f59e0b;
}

/* === PR1: Notification transparency panel === */
.notification-panel {
  margin: 1rem 0 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.notification-panel--sent {
  border-color: #86efac;
  background: #f0fdf4;
}

.notification-panel--info {
  border-color: #bae6fd;
  background: #f0f9ff;
}

.notification-panel--pending {
  border-color: #fcd34d;
  background: #fffbeb;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.notification-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9375rem;
}

.notification-title i {
  color: #475569;
}

.notification-status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
}

.notification-status.sent {
  background: #dcfce7;
  color: #166534;
}

.notification-status.info {
  background: #e0f2fe;
  color: #075985;
}

.notification-status.pending {
  background: #fef3c7;
  color: #92400e;
}

.notification-recipient {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.625rem 0.875rem;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #1e293b;
}

.notification-recipient.no-recipient {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.recipient-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.recipient-email {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace;
  background: #f1f5f9;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  color: #1e293b;
}

.recipient-source {
  font-size: 0.75rem;
  color: #64748b;
}

.recipient-contact {
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.notification-reasons {
  font-size: 0.8125rem;
  color: #475569;
  margin-bottom: 0.75rem;
}

.notification-reasons ul {
  margin: 0.25rem 0 0 1.25rem;
  padding: 0;
}

.notification-reasons li {
  margin: 0.125rem 0;
}

.notification-history {
  font-size: 0.8125rem;
  color: #475569;
  margin-bottom: 0.75rem;
}

.notification-history ul {
  margin: 0.25rem 0 0 1.25rem;
  padding: 0;
  list-style: none;
}

.notification-history li {
  margin: 0.25rem 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
}

.notification-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.notification-actions .btn-sm {
  padding: 0.4rem 0.875rem;
  font-size: 0.8125rem;
}

.notification-actions .btn-outline {
  background: white;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.notification-actions .btn-outline:hover:not(:disabled) {
  background: #f1f5f9;
}

.notification-actions .btn-primary:disabled,
.notification-actions .btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.text-success { color: #10b981; }
.text-danger { color: #ef4444; }

/* PR2: Send drawer (override To/CC, pick contact) */
.send-drawer {
  margin-top: 0.75rem;
  padding: 1rem;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.send-drawer .drawer-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #1e293b;
  font-size: 0.9375rem;
}

.send-drawer .drawer-header i {
  color: #3b82f6;
}

.send-drawer .drawer-row {
  margin-bottom: 0.75rem;
}

.send-drawer .drawer-row label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.25rem;
}

.send-drawer .drawer-row .req {
  color: #ef4444;
}

.send-drawer .form-control {
  width: 100%;
  padding: 0.5rem 0.625rem;
  font-size: 0.875rem;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: white;
}

.send-drawer .form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.send-drawer .cc-input-row {
  display: flex;
  gap: 0.5rem;
}

.send-drawer .cc-input-row .form-control {
  flex: 1;
}

.send-drawer .cc-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.5rem;
}

.send-drawer .cc-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: #e0f2fe;
  color: #0c4a6e;
  border-radius: 999px;
  font-size: 0.8125rem;
}

.send-drawer .cc-remove {
  background: transparent;
  border: 0;
  cursor: pointer;
  color: #0369a1;
  padding: 0;
  display: inline-flex;
  align-items: center;
}

.send-drawer .cc-remove:hover {
  color: #0c4a6e;
}

.send-drawer .cc-hint {
  display: block;
  margin-top: 0.375rem;
  color: #64748b;
  font-size: 0.75rem;
}

.send-drawer .drawer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.history-trigger {
  display: inline-block;
  padding: 0 0.375rem;
  border-radius: 3px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.history-trigger--auto {
  background: #fef3c7;
  color: #92400e;
}

.history-trigger--manual {
  background: #dbeafe;
  color: #1e40af;
}
</style>

